import { useTranslation } from "react-i18next";
import GitFitGridLoader from "@/components/GitFitGridLoader";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAppSession } from "@/providers/AppSessionProvider";
import { useProfile } from "@/hooks/useProfile";
import { ArrowLeft, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MobileBottomNav from "@/components/MobileBottomNav";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import CompareGraphs from "@/components/CompareGraphs";
import CompareHeadToHead from "@/components/CompareHeadToHead";
import { toast } from "sonner";

const Compare = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ready, session, isAuthenticated } = useAppSession();
  const user = session?.user || null;
  const [compareMode, setCompareMode] = useState<"self" | "versus">("self");

  // Redirect to auth if not authenticated
  useEffect(() => {
    if (ready !== 'ready') return;
    
    if (!isAuthenticated) {
      console.log("[Compare] No session found, redirecting to auth");
      navigate("/auth", { replace: true });
    }
  }, [ready, isAuthenticated, navigate]);

  // Use centralized profile hook for username check
  const { data: userProfile, isLoading: isLoadingUserProfile } = useProfile(user?.id);

  // Redirect if no username
  useEffect(() => {
    if (ready !== 'ready' || !user || isLoadingUserProfile) return;
    
    if (userProfile && userProfile.username === null) {
      console.log("[Compare] No username found, redirecting to auth for onboarding");
      localStorage.removeItem("onboarding_complete");
      navigate("/auth", { replace: true });
    }
  }, [ready, user, userProfile, isLoadingUserProfile, navigate]);

  // Check for mode in URL params
  useEffect(() => {
    const mode = searchParams.get("mode");
    if (mode === "versus") {
      setCompareMode("versus");
    } else {
      setCompareMode("self");
    }
  }, [searchParams]);

  // Get opponent ID from URL
  const opponentId = searchParams.get("opponent") || undefined;

  if (ready === 'checking') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <GitFitGridLoader className="mx-auto" />
          <p className="mt-4 text-muted-foreground">{t('ui.text.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-6">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />{t('ui.text.back_to_dashboard')}</Button>
          
          <h1 className="text-3xl font-bold mb-2">{t('ui.text.compare_graphs')}</h1>
          <p className="text-muted-foreground">{t('ui.text.analyze_correlations_between_metrics_or_compete_he')}</p>
        </div>

        {/* Mode Tabs */}
        <Tabs value={compareMode} onValueChange={(value) => setCompareMode(value as "self" | "versus")} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="self" className="gap-2">
              <TrendingUp className="w-4 h-4" />{t('ui.text.self_comparison')}</TabsTrigger>
            <TabsTrigger value="versus" className="gap-2">
              <Minus className="w-4 h-4 rotate-90" />{t('ui.text.head_to_head')}</TabsTrigger>
          </TabsList>

          <TabsContent value="self" className="mt-0">
            <Card className="p-6">
              <div className="mb-4">
                <h2 className="text-xl font-semibold mb-2">{t('ui.text.compare_your_metrics')}</h2>
                <p className="text-sm text-muted-foreground">
                  Select two of your metrics to see where there's strong positive or negative correlation.
                  This can help you understand how different habits affect each other.
                </p>
              </div>
              <CompareGraphs userId={user.id} />
            </Card>
          </TabsContent>

          <TabsContent value="versus" className="mt-0">
            <Card className="p-6">
              <div className="mb-4">
                <h2 className="text-xl font-semibold mb-2">{t('ui.text.head_to_head_comparison')}</h2>
                <p className="text-sm text-muted-foreground">
                  Compare your performance against another user. Select any two metrics (they don't have to match)
                  and see who dominated each day in a red vs. blue battle.
                </p>
              </div>
              <CompareHeadToHead currentUserId={user.id} preselectedOpponentId={opponentId} />
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <MobileBottomNav />
    </div>
  );
};

export default Compare;
